import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGet } from '@claritio/frontend/lib/api'
import { useOrgId } from '@claritio/frontend/lib/auth'
import { useInterval } from '@claritio/frontend/lib/useInterval'
import { Badge } from '@claritio/frontend/components/ui/badge'
import type { OrganizationProfileData } from '@claritio/frontend/types'

type Completion = Pick<OrganizationProfileData, 'overall_percent' | 'filled_count' | 'total_count'>

// Profile completion is progress, not an error: neutral until it is complete.
function variantFor(percent: number) {
  return percent >= 100 ? 'success' as const : 'muted' as const
}

// ENG-86: ambient completion signal for the Organization Profile, visible from
// anywhere in the shell (not just the profile page itself). Mirrors
// NotificationBell's self-contained poll pattern. Refetches on org-context
// change and on route change (so returning from the ENG-83 editor after a save
// reflects instantly) with a periodic poll as a backstop for a long-idle tab.
export default function ProfileCompletionIndicator() {
  const { t } = useTranslation('admin')
  const location = useLocation()
  const orgId = useOrgId()
  const [loaded, setData] = useState<Completion | null>(null)
  // Derived: outside a workspace there is nothing to show, so gate at the point
  // of use rather than clearing state inside the effect.
  const data = orgId ? loaded : null

  const load = useCallback(() => {
    if (!orgId) return
    apiGet<OrganizationProfileData>('/api/v1/organization_profile', orgId)
      .then(({ overall_percent, filled_count, total_count }) => setData({ overall_percent, filled_count, total_count }))
      .catch(() => setData(null))
  }, [orgId])

  useEffect(load, [load, location.pathname])
  useInterval(load, 45_000)

  if (!data) return null

  return (
    <Link
      to={`/organization-profile?org_id=${orgId}`}
      title={t('shell.profileCompletion.tooltip', { filled: data.filled_count, total: data.total_count })}
    >
      <Badge variant={variantFor(data.overall_percent)}>{data.overall_percent}%</Badge>
    </Link>
  )
}
